/** Maximum positive DECIMAL(13,2) value expressed in kopecks. */
export const MAX_MONEY_KOPECKS = 9_999_999_999_999;

/** Parameters controlling exact normalization of one editable money draft. */
export type EditableMoneyNormalizationOptions = {
  /** Raw value typed into an editable control before the API payload is built. */
  value: string;
  /** Whether this field accepts a leading minus sign for a negative exact amount. */
  allowNegative: boolean;
};

/**
 * Checks whether a manual-input segment contains one or more decimal digits.
 *
 * @param value Untrimmed ruble or fractional segment.
 * @returns True only for a non-empty ASCII digit sequence without signs, grouping or exponent notation.
 */
function isManualInputDigitSequence(value: string): boolean {
  if (value === '') return false;
  for (const character of value) if (character < '0' || character > '9') return false;
  return true;
}

/**
 * Parses a canonical API money value without floating-point arithmetic.
 *
 * @param value Signed decimal string with exactly two fractional digits.
 * @returns Exact signed kopecks within the DECIMAL(13,2) range, or null for invalid input.
 */
export function parseCanonicalMoney(value: string): number | null {
  const parts = value.split('.');
  if (parts.length !== 2 || parts[0].length === 0 || parts[1].length !== 2) return null;

  const rubles = Number(parts[0]);
  const kopecks = Number(parts[1]);
  if (!Number.isInteger(rubles) || !Number.isInteger(kopecks)) return null;

  const amount = parts[0].startsWith('-') ? (rubles * 100) - kopecks : (rubles * 100) + kopecks;
  return Number.isSafeInteger(amount) && Math.abs(amount) <= MAX_MONEY_KOPECKS ? amount : null;
}

/**
 * Normalizes a manually entered positive ruble amount for a payment query.
 *
 * Manual input accepts whole rubles or a comma followed by exactly two kopeck
 * digits. The returned canonical value always uses a period for API boundaries.
 *
 * @param value Amount using optional comma-separated two-digit kopecks.
 * @returns Canonical positive two-decimal string within range, or null for empty, zero, signed or invalid input.
 */
export function normalizeMoneyInput(value: string): string | null {
  const trimmedValue = value.trim();
  if (trimmedValue === '' || trimmedValue.includes(' ') || trimmedValue.startsWith('-') || trimmedValue.startsWith('+')) return null;

  const parts = trimmedValue.split(',');
  if (parts.length > 2 || parts[0] === '' || (parts.length === 2 && parts[1].length !== 2)) return null;
  if (!isManualInputDigitSequence(parts[0]) || (parts.length === 2 && !isManualInputDigitSequence(parts[1]))) return null;

  const fractionalPart = parts.length === 1 ? '00' : parts[1];
  const canonicalValue = `${parts[0]}.${fractionalPart}`;
  const kopecks = parseCanonicalMoney(canonicalValue);

  return kopecks !== null && kopecks > 0 ? canonicalValue : null;
}

/**
 * Normalizes an editable exact money draft to the canonical DECIMAL(13,2) API representation.
 *
 * The function accepts a whole-ruble value, an exact dot/comma fractional draft or a canonical
 * value. It uses only string processing and the shared exact kopeck primitives: no binary
 * floating-point amount is created. Invalid values remain null so the caller can preserve the
 * visible draft and show a field-level error.
 *
 * @param options Draft value and the field-specific negative-value policy.
 * @returns Canonical signed two-decimal string within the DECIMAL(13,2) range, or null when invalid.
 */
export function normalizeEditableMoneyInput({
  value,
  allowNegative,
}: EditableMoneyNormalizationOptions): string | null {
  const trimmedValue = value.trim();
  if (
    trimmedValue === '' ||
    trimmedValue.includes(' ') ||
    trimmedValue.startsWith('+')
  ) {
    return null;
  }

  const isNegative = trimmedValue.startsWith('-');
  if (isNegative && !allowNegative) return null;

  const unsignedValue = isNegative ? trimmedValue.slice(1) : trimmedValue;
  if (unsignedValue === '') return null;

  const containsDot = unsignedValue.includes('.');
  const containsComma = unsignedValue.includes(',');
  if (containsDot && containsComma) return null;

  let parts: string[];
  if (containsDot) {
    parts = unsignedValue.split('.');
  } else if (containsComma) {
    parts = unsignedValue.split(',');
  } else {
    parts = [unsignedValue];
  }
  if (parts.length > 2 || parts[0] === '') return null;

  const [rubles, fractionalPart] = parts;
  if (
    !isManualInputDigitSequence(rubles) ||
    (fractionalPart !== undefined &&
      (fractionalPart.length !== 2 || !isManualInputDigitSequence(fractionalPart)))
  ) {
    return null;
  }

  const canonicalValue = `${isNegative ? '-' : ''}${rubles}.${fractionalPart ?? '00'}`;
  const kopecks = parseCanonicalMoney(canonicalValue);
  if (kopecks === null || (!allowNegative && kopecks < 0)) return null;

  return serializeMoney(kopecks) === canonicalValue ? canonicalValue : null;
}

/**
 * Serializes an exact monetary amount for API and query boundaries.
 *
 * @param kopecks Signed integer kopecks.
 * @returns Canonical signed two-decimal string within range, or null for a non-integer or out-of-range value.
 */
export function serializeMoney(kopecks: number): string | null {
  if (!Number.isSafeInteger(kopecks) || Math.abs(kopecks) > MAX_MONEY_KOPECKS) return null;
  const absoluteKopecks = Math.abs(kopecks);
  return `${kopecks < 0 ? '-' : ''}${Math.floor(absoluteKopecks / 100)}.${String(absoluteKopecks % 100).padStart(2, '0')}`;
}
